'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import Avatar from 'apps/cdp-web-demo/app/icons/avatar.svg';
import Checkmark from 'apps/cdp-web-demo/app/icons/checkmarkCircle.svg';
import Copy from 'apps/cdp-web-demo/app/icons/copy.svg';
import { AccountCreationType } from 'apps/cdp-web-demo/app/types';

import styles from './UserAddress.module.css';

type Props = {
  accountCreationType: AccountCreationType;
  userAddress?: string;
};

function UserAddress({ accountCreationType, userAddress }: Props) {
  const [isCopied, setIsCopied] = useState(false);

  const addressFormatted = useMemo(() => {
    if (accountCreationType === 'solana') {
      return `${userAddress?.slice(0, 4)}...${userAddress?.slice(-4)}`;
    }
    return `0x${`${userAddress?.slice(2, 6)}...${userAddress?.slice(-4)}`.toUpperCase()}`;
  }, [userAddress, accountCreationType]);

  const copyAddress = useCallback(async () => {
    if (!userAddress) return;
    try {
      // eslint-disable-next-line compat/compat
      await navigator.clipboard?.writeText(userAddress);
      setIsCopied(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [userAddress]);

  const handleClick = useCallback(() => {
    void copyAddress();
  }, [copyAddress]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (isCopied) {
      timeout = setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isCopied]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div onClick={handleClick} className={`${styles['user-address-button']} `}>
      <button type="button" className="sr-only" onClick={handleClick}>
        Copy address
      </button>
      {isCopied ? (
        <Checkmark role="img" aria-hidden="true" className={styles.checkmark} />
      ) : (
        <>
          <Avatar role="img" aria-hidden="true" className={styles.avatar} />
          <Copy role="img" aria-hidden="true" className={styles.copy} />
        </>
      )}
      <span>
        <span className={styles['user-account-type']}>
          {accountCreationType === 'solana' && 'Solana'}
          {accountCreationType === 'evm-smart' && 'EVM Smart'}
          {accountCreationType === 'evm-eoa' && 'EVM EOA'}
        </span>
        <span className={styles['user-address']}>{addressFormatted}</span>
      </span>
    </div>
  );
}

export { UserAddress };
